import React, { useEffect, useState } from 'react';

import { Button, Group, Modal, Radio, Select, Stack, Text, Title } from '@mantine/core';

import { useLazyGetPatientSchedulesQuery } from '@/store/slices/api/patientsApiSlice';

import { findDeviceActiveSchedules } from './activeSchedules';
import BillableSchedule from './BillableSchedule';
import CancelActiveSchedules from './CancelActiveSchedules';
import CancelERITracking from './CancelERITracking';
import styles from './DeviceScheduleModal.module.scss';
import InClinicSchedule from './InClinicSchedule';
import PhysicianRequestedSchedule from './PhysicianRequestedSchedule';
import SetERITracking from './SetERITracking';
import { EditScheduleOption } from './types';

const VENDOR_MAPPING: Record<string, string> = {
  medtronic: 'Medtronic',
  abbott: 'Abbott',
  biotronik: 'Biotronik',
  boston_scientific: 'Boston Scientific',
  sorin: 'Sorin'
};

interface DeviceInfo {
  modelNumber: string;
  serialNumber: string;
  vendor?: string;
  type?: string;
  deviceId: string;
  isEriTracked?: boolean;
}

interface DeviceScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  deviceInfo?: DeviceInfo;
  patientSFId?: string;
  shouldShowDeviceSelector?: boolean;
  isAppointmentManagedInRs?: boolean;
  /** Clinic workflow includes Automated Scheduling. Rhythm owns the transmission schedule. */
  canSetSchedules?: boolean;
  availableDevices?: DeviceInfo[];
}

interface DeviceSelectorProps {
  availableDevices: DeviceInfo[];
  selectedDevice: DeviceInfo | null;
  setSelectedDevice: (device: DeviceInfo | null) => void;
  onClose: () => void;
  onNext: () => void;
  loading: boolean;
}

interface AvailableActionsProps {
  selected: EditScheduleOption | null;
  setSelected: (value: EditScheduleOption | null) => void;
  onClose: () => void;
  onNext: () => void;
  onBack?: () => void;
  loading: boolean;
  showBackButton?: boolean;
  isEriTracked?: boolean;
  isAppointmentManagedInRs?: boolean;
  hasActiveSchedules?: boolean;
  canSetSchedules?: boolean;
}

const getOptionList = (
  isEriTracked?: boolean,
  isAppointmentManagedInRs?: boolean,
  hasActiveSchedules?: boolean,
  canSetSchedules?: boolean
): { key: EditScheduleOption; label: string; description: string }[] => {
  const baseOptions: { key: EditScheduleOption; label: string; description: string }[] = [
    ...(canSetSchedules
      ? [
          {
            key: 'billable' as const,
            label: isAppointmentManagedInRs ? 'Create Billable Schedule' : 'Edit Schedule',
            description: isAppointmentManagedInRs
              ? 'Set Billable appointment for patient, which will automatically adjust appointments schedules'
              : 'Runs the scheduler for this device when you continue. It can move every date.'
          }
        ]
      : []),
    {
      key: 'in_clinic',
      label: 'Create In Clinic Schedule',
      description:
        'Set In Clinic appointment for patient, which will automatically adjust appointments schedules'
    },
    {
      key: 'physician_requested',
      label: 'Create Physician Requested Schedule',
      description: 'Set Physician requested non-billable appointment for patient'
    }
  ];

  if (hasActiveSchedules) {
    baseOptions.push({
      key: 'cancel',
      label: 'Cancel Active Schedules',
      description: 'Cancels active schedules for this device'
    });
  }

  if (isEriTracked === true) {
    baseOptions.push({
      key: 'cancel_eri_tracking',
      label: 'Cancel ERI Tracking',
      description: 'Cancels ERI Tracking Schedule and remove ERI Tracking from device'
    });
  } else if (isEriTracked === false) {
    baseOptions.push({
      key: 'set_eri_tracking',
      label: 'Set ERI Tracking',
      description: 'Set custom ERI tracking schedule based on account and patient settings'
    });
  }

  return baseOptions;
};

const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  availableDevices,
  selectedDevice,
  setSelectedDevice,
  onClose,
  onNext,
  loading
}) => {
  const getDeviceDisplayTitle = (device: DeviceInfo) => {
    const vendor = VENDOR_MAPPING[device.vendor || ''] ?? device.vendor;
    return `${vendor} ${device.type}`;
  };

  const deviceOptions = availableDevices.map((device) => ({
    value: device.deviceId,
    label: getDeviceDisplayTitle(device)
  }));

  const handleDeviceChange = (value: string | null) => {
    const device = availableDevices.find((d) => d.deviceId === value) || null;
    setSelectedDevice(device);
  };

  return (
    <Stack gap={'xs'}>
      <Stack gap={'lg'} px={20} pt={16}>
        <Title order={5} fw={600} c={'plum.9'}>
          Select Device
        </Title>
        <div>
          <Text size="sm" fw={500} mb="xs" c={'plum.8'}>
            Device
          </Text>
          <Select
            placeholder="Select a device"
            data={deviceOptions}
            value={selectedDevice?.deviceId || null}
            onChange={handleDeviceChange}
            searchable
            clearable
            disabled={loading}
            size="sm"
          />
        </div>
      </Stack>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onClose}
          disabled={loading}
          className={styles.modalButton}>
          Cancel
        </Button>
        <Button
          color="plum.9"
          onClick={onNext}
          disabled={!selectedDevice || loading}
          className={styles.modalButton}>
          Next
        </Button>
      </Group>
    </Stack>
  );
};

const AvailableActions: React.FC<AvailableActionsProps> = ({
  selected,
  setSelected,
  onClose,
  onNext,
  onBack,
  loading,
  showBackButton = false,
  isEriTracked,
  isAppointmentManagedInRs,
  hasActiveSchedules,
  canSetSchedules
}) => {
  const optionList = getOptionList(
    isEriTracked,
    isAppointmentManagedInRs,
    hasActiveSchedules,
    canSetSchedules
  );

  return (
    <Stack gap={'xs'}>
      <Stack gap={'xs'} px={20} pt={16}>
        <Title order={5} fw={600} mb={'xs'} c={'plum.9'}>
          Available Actions
        </Title>
        <Radio.Group
          value={selected}
          onChange={(value) => setSelected(value as EditScheduleOption)}
          size="sm">
          {optionList.map((option) => (
            <Radio
              key={option.key}
              value={option.key}
              size="xs"
              label={
                <div>
                  <Text fw={600} size="sm" c={'plum.6'} lh={1} mb={2}>
                    {option.label}
                  </Text>
                  <Text size="xs" c="plum.4">
                    {option.description}
                  </Text>
                </div>
              }
              className={styles.optionRadio}
              data-selected={selected === option.key}
              classNames={{
                label: styles.optionRadioLabel
              }}
            />
          ))}
        </Radio.Group>
      </Stack>
      <Group className={styles.buttonGroup} justify="flex-end">
        {showBackButton && (
          <Button
            variant="outline"
            color="plum.9"
            onClick={onBack}
            disabled={loading}
            className={styles.modalButton}>
            Back
          </Button>
        )}
        <Button
          variant="outline"
          color="plum.9"
          onClick={onClose}
          disabled={loading}
          className={styles.modalButton}>
          Cancel
        </Button>
        <Button
          color="plum.9"
          onClick={onNext}
          disabled={!selected || loading}
          className={styles.modalButton}>
          Next
        </Button>
      </Group>
    </Stack>
  );
};

const DeviceScheduleModal: React.FC<DeviceScheduleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  deviceInfo,
  patientSFId,
  isAppointmentManagedInRs,
  canSetSchedules = false,
  shouldShowDeviceSelector = false,
  availableDevices = []
}) => {
  const promptForDevice = shouldShowDeviceSelector && availableDevices.length > 1;
  const soleDevice = availableDevices.length === 1 ? availableDevices[0] : null;
  const [selectedDevice, setSelectedDevice] = useState<DeviceInfo | null>(deviceInfo || soleDevice);
  const [selected, setSelected] = useState<EditScheduleOption | null>(null);
  const [step, setStep] = useState<'device' | 'option' | EditScheduleOption>(
    promptForDevice ? 'device' : 'option'
  );
  const [loading, setLoading] = useState(false);

  const currentDeviceInfo = selectedDevice || deviceInfo || soleDevice;
  const activeDeviceId = currentDeviceInfo?.deviceId;
  const [lookupSchedules] = useLazyGetPatientSchedulesQuery();
  const [hasActiveSchedules, setHasActiveSchedules] = useState(false);

  useEffect(() => {
    if (!isOpen || !patientSFId || !activeDeviceId) {
      setHasActiveSchedules(false);
      return;
    }
    let cancelled = false;
    setHasActiveSchedules(false);
    void findDeviceActiveSchedules(
      (query) => lookupSchedules({ patientId: patientSFId, ...query }).unwrap(),
      activeDeviceId
    )
      .then((found) => {
        if (!cancelled) setHasActiveSchedules(found);
      })
      .catch(() => {
        if (!cancelled) setHasActiveSchedules(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeDeviceId, isOpen, lookupSchedules, patientSFId]);

  useEffect(() => {
    if (!isOpen || promptForDevice) return;
    if (soleDevice) setSelectedDevice((current) => current ?? soleDevice);
    setStep((current) => (current === 'device' ? 'option' : current));
  }, [isOpen, promptForDevice, soleDevice]);

  const handleDeviceNext = () => {
    setStep('option');
  };

  const handleOptionNext = () => {
    if (selected) setStep(selected);
  };

  const handleBackToOptions = () => {
    setStep('option');
  };

  const handleBackToDevice = () => {
    if (promptForDevice) {
      setStep('device');
    } else {
      setStep('option');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setStep(promptForDevice ? 'device' : 'option');
      setSelected(null);
      setSelectedDevice(deviceInfo || soleDevice);
      onClose();
    }
  };

  const handleSuccess = () => {
    handleClose();
    onSuccess?.();
  };

  const renderStepContent = () => {
    switch (step) {
      case 'device':
        return (
          <DeviceSelector
            availableDevices={availableDevices}
            selectedDevice={selectedDevice}
            setSelectedDevice={setSelectedDevice}
            onClose={handleClose}
            onNext={handleDeviceNext}
            loading={loading}
          />
        );
      case 'option':
        return (
          <AvailableActions
            selected={selected}
            setSelected={setSelected}
            onClose={handleClose}
            onNext={handleOptionNext}
            onBack={promptForDevice ? handleBackToDevice : undefined}
            loading={loading}
            showBackButton={promptForDevice}
            isEriTracked={currentDeviceInfo?.isEriTracked}
            isAppointmentManagedInRs={isAppointmentManagedInRs}
            hasActiveSchedules={hasActiveSchedules}
            canSetSchedules={canSetSchedules}
          />
        );
      case 'billable':
        return (
          <BillableSchedule
            onBack={handleBackToOptions}
            onClose={handleClose}
            onSuccess={handleSuccess}
            selectedOption={step}
            loading={loading}
            setLoading={setLoading}
            patientSFId={patientSFId}
            deviceInfo={currentDeviceInfo}
            isAppointmentManagedInRs={isAppointmentManagedInRs}
          />
        );
      case 'in_clinic':
        return (
          <InClinicSchedule
            onBack={handleBackToOptions}
            onClose={handleClose}
            onSuccess={handleSuccess}
            selectedOption={step}
            loading={loading}
            setLoading={setLoading}
            patientSFId={patientSFId}
            deviceInfo={currentDeviceInfo}
            runSchedulerAfterCreate={canSetSchedules}
          />
        );
      case 'physician_requested':
        return (
          <PhysicianRequestedSchedule
            onBack={handleBackToOptions}
            onClose={handleClose}
            onSuccess={handleSuccess}
            selectedOption={step}
            loading={loading}
            patientSFId={patientSFId}
            deviceInfo={currentDeviceInfo}
            setLoading={setLoading}
            runSchedulerAfterCreate={canSetSchedules}
          />
        );
      case 'cancel':
        return (
          <CancelActiveSchedules
            onBack={handleBackToOptions}
            onClose={handleClose}
            onSuccess={handleSuccess}
            selectedOption={step}
            loading={loading}
            setLoading={setLoading}
            patientSFId={patientSFId}
            deviceInfo={currentDeviceInfo}
          />
        );
      case 'set_eri_tracking':
        return (
          <SetERITracking
            onBack={handleBackToOptions}
            onClose={handleClose}
            onSuccess={handleSuccess}
            selectedOption={step}
            loading={loading}
            setLoading={setLoading}
            deviceInfo={currentDeviceInfo}
          />
        );
      case 'cancel_eri_tracking':
        return (
          <CancelERITracking
            onBack={handleBackToOptions}
            onClose={handleClose}
            onSuccess={handleSuccess}
            selectedOption={step}
            loading={loading}
            setLoading={setLoading}
            deviceInfo={currentDeviceInfo!}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      size="lg"
      withCloseButton={!loading}
      closeOnClickOutside={false}
      closeOnEscape={!loading}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody
      }}
      title={
        <Text fw={600} size="lg" c="plum.9">
          Edit Schedule
        </Text>
      }>
      <React.Suspense fallback={<Text>Loading...</Text>}>{renderStepContent()}</React.Suspense>
    </Modal>
  );
};

export default DeviceScheduleModal;
